import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DRINKS, INGREDIENTS } from '@/lib/gameData';
import { hapticNotification } from '@/lib/telegram';
import { useCafe3dStore } from '@/stores/cafe3dStore';

export function ResultOverlay() {
  const { t } = useTranslation();
  const lastResult = useCafe3dStore((s) => s.lastResult);
  const sceneState = useCafe3dStore((s) => s.sceneState);
  const currentCustomer = useCafe3dStore((s) => s.currentCustomer);
  const finishResultAnimation = useCafe3dStore((s) => s.finishResultAnimation);

  const showOverlay = sceneState === 'success' || sceneState === 'wrong' || sceneState === 'timeout';

  useEffect(() => {
    if (!showOverlay) return;
    // Telegram haptic feedback theo kết quả (no-op ngoài Telegram).
    if (sceneState === 'success') hapticNotification('success');
    else if (sceneState === 'wrong') hapticNotification('error');
    else if (sceneState === 'timeout') hapticNotification('warning');

    // Auto advance after dopamine sequence (3.5s for success, 3s for wrong với hint, 2s timeout)
    const delay = sceneState === 'success' ? 3000 : sceneState === 'wrong' ? 3000 : 2000;
    const id = setTimeout(() => finishResultAnimation(), delay);
    return () => clearTimeout(id);
  }, [sceneState, showOverlay, finishResultAnimation]);

  if (!showOverlay || !lastResult) return null;

  // Tên món GIỮ tiếng Việt trong mọi locale (bản sắc cà phê Việt) → name_vi.
  const expectedDrink = currentCustomer ? DRINKS[currentCustomer.drink] : null;

  const isSuccess = lastResult.status === 'correct';
  const isWrong = lastResult.status === 'wrong';
  const isTimeout = lastResult.status === 'timeout';

  return (
    <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center">
      <div
        className="text-center px-6 py-4 rounded-2xl backdrop-blur-sm animate-pop-in"
        style={{
          backgroundColor: isSuccess ? 'rgba(92, 138, 79, 0.95)' : 'rgba(255, 243, 231, 0.95)',
        }}
      >
        {isSuccess && (
          <>
            <h2 className="font-baloo font-bold text-3xl text-cream-50 mb-1">
              {t('result.success_title')}
            </h2>
            <div className="text-cream-50/90 text-sm space-y-0.5">
              <div>+{lastResult.reward.coin} {t('hud.bean')}</div>
              {lastResult.reward.tip > 0 && (
                <div className="text-yellow-light font-semibold">
                  {t('result.tip_quick_text', { tip: lastResult.reward.tip })}
                </div>
              )}
              <div className="text-sm">
                +{lastResult.reward.reputation} ⭐
              </div>
            </div>
          </>
        )}
        {isWrong && (
          <>
            <h2 className="font-baloo font-semibold text-xl text-coffee-900 mb-1">
              {t('result.wrong_title')}
            </h2>
            {expectedDrink && (
              <div className="mt-1 mb-2 px-3 py-2 rounded-lg bg-coffee-900/5">
                <p className="text-xs text-coffee-700/80 mb-1">
                  {t('result.wrong_wanted', { drink: expectedDrink.name_vi })}
                </p>
                <div className="flex items-center justify-center gap-1 text-base">
                  {expectedDrink.recipe.map((ing, i) => (
                    <span key={ing} className="flex items-center gap-1">
                      <span title={t(`ingredient.${ing}`)}>
                        {INGREDIENTS[ing].emoji}
                      </span>
                      {i < expectedDrink.recipe.length - 1 && (
                        <span className="text-coffee-700/40 text-xs">+</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="text-sm text-coffee-700">
              {lastResult.reward.coin > 0
                ? t('result.wrong_got_bean', { coin: lastResult.reward.coin })
                : t('result.wrong_no_bean')}
            </p>
          </>
        )}
        {isTimeout && (
          <>
            <h2 className="font-baloo font-semibold text-xl text-coffee-900 mb-1">
              {t('result.timeout_title')}
            </h2>
            <p className="text-sm text-coffee-700">
              {t('result.timeout_reputation')}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
